import type { Item } from 'src/types';
import { PromiseQueue } from 'src/util/promiseQueue';
import type { ItemsLoadProgress } from './loadProgress';

/** One page of `GET /item`, as the API returns it. */
export type ItemPageResponse = {
  items: Item[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
};

/** How many item pages are in flight at once. */
const MAX_CONCURRENT_ITEM_PAGES = 4;

/** Flatten pages 1..`throughPage` into one list, keeping one copy of each item. */
const flattenPages = (pageItems: (Item[] | undefined)[], throughPage: number): Item[] => {
  // Pages are separate requests, so an edit landing between two of them shifts
  // the offsets underneath us and an item can arrive on both. Keying by `key`
  // keeps one copy of each and leaves the list in page order.
  const byKey = new Map<string, Item>();
  for (let page = 1; page <= throughPage; page += 1) {
    for (const item of pageItems[page - 1] ?? []) byKey.set(item.key, item);
  }
  return [...byKey.values()];
};

/**
 * Every item on a job, assembled from the API's fixed-size pages.
 *
 * `GET /item` serves at most 50 items per page. It used to serve a whole job
 * at once, which on a job of a few thousand items meant one query reading
 * every comparable on the job -- minutes of database time, and a response
 * large enough that the request simply timed out.
 *
 * `onProgress` is called as pages arrive so the screen can render the first one
 * immediately instead of waiting on the last. It only ever reports a run of
 * pages starting at the first: pages resolve out of order, and publishing one
 * that fills a gap in the middle would make rows jump under a reviewer's cursor
 * mid-read. Growing only at the end is also what makes a spinner beneath the
 * list the honest place to say more is coming.
 */
export const collectItemPages = async (
  fetchPage: (page: number) => Promise<ItemPageResponse | null>,
  onProgress?: (items: Item[], progress: ItemsLoadProgress) => void
): Promise<Item[]> => {
  const firstPage = await fetchPage(1);

  if (!firstPage) throw new Error('Job not found');

  const { totalPages, total: totalItems } = firstPage;
  const pageItems: (Item[] | undefined)[] = new Array(totalPages);
  pageItems[0] = firstPage.items;

  let publishedThrough = 0;
  const publish = () => {
    let next = publishedThrough;
    while (next < totalPages && pageItems[next] !== undefined) next += 1;
    if (next === publishedThrough) return;

    publishedThrough = next;
    const items = flattenPages(pageItems, publishedThrough);
    onProgress?.(items, {
      loadedItems: items.length,
      totalItems,
      loadedPages: publishedThrough,
      totalPages,
    });
  };

  publish();

  if (totalPages <= 1) return flattenPages(pageItems, totalPages);

  // The first page reports how many there are, so the rest can go out together
  // rather than one after another -- bounded, so a wide job does not open
  // hundreds of requests at once.
  const remainingPages = Array.from({ length: totalPages - 1 }, (_, index) => index + 2);
  const promiseQueue = new PromiseQueue<ItemPageResponse | null>({
    maxConcurrent: MAX_CONCURRENT_ITEM_PAGES,
  });

  await Promise.all(
    promiseQueue.enqueue(
      remainingPages.map((page) => async () => {
        const response = await fetchPage(page);
        if (!response) throw new Error('Job not found');

        pageItems[page - 1] = response.items;
        publish();
        return response;
      })
    )
  );

  return flattenPages(pageItems, totalPages);
};
